import { QueryParams } from '@/App'
import useQuery from '@/hooks/useQuery'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { setDefaultOptions } from 'date-fns'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import enLocale from 'date-fns/locale/en-GB'
import esLocale from 'date-fns/locale/es'
import itLocale from 'date-fns/locale/it'
import frLocale from 'date-fns/locale/fr'

export enum Languages {
  Es = 'es',
  En = 'en',
  It = 'it',
  Fr = 'fr',
}

const localeMap = {
  en: enLocale,
  es: esLocale,
  it: itLocale,
  fr: frLocale,
}

export const API_LOCALE_MAP: { [key in Languages]: string } = {
  [Languages.En]: 'gb',
  [Languages.Es]: 'es',
  [Languages.It]: 'it',
  [Languages.Fr]: 'fr',
}

const LanguageContext = createContext<{
  language: Languages
  changeLanguage: (language: Languages) => void
}>({} as any)

const LanguageContextProvider: React.FC = ({ children }) => {
  const { i18n } = useTranslation()
  const { queryParams, setURLQueryParams } = useQuery()
  const paramLang = queryParams?.[QueryParams.LangId] as Languages

  const [language, setLanguage] = useState<Languages>(() => {
    let defaultLang = paramLang || (i18n.language as Languages)
    if (!Object.values(Languages).includes(defaultLang)) defaultLang = Languages.En
    return defaultLang
  })

  useEffect(() => {
    i18n.changeLanguage(language)
    setDefaultOptions({
      locale: localeMap[language as keyof typeof localeMap],
    })
  }, [])

  const changeLanguage = (language: Languages) => {
    i18n.changeLanguage(language)
    setLanguage(language)
    setDefaultOptions({
      locale: localeMap[language as keyof typeof localeMap],
    })

    setURLQueryParams({
      langid: language,
    })
  }

  return (
    <LanguageContext.Provider
      value={{
        language,
        changeLanguage,
      }}
    >
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={localeMap[i18n.language as keyof typeof localeMap]}
      >
        {children}
      </LocalizationProvider>
    </LanguageContext.Provider>
  )
}
export default LanguageContextProvider
export const useLanguageContext = () => useContext(LanguageContext)
